import { authFetch } from './client';
// Server-side AGENT runs (HS-190). The backend runs the LLM agent itself — see
// product_backend/app/services/agent_runner/. The client starts a run from a
// message and then OBSERVES it (see src/agent/useAgentRun.js); it does not
// execute tools for a server run, which is the whole difference from the
// browser-driven path in panels/chatRounds.js.

// authFetch throws Error(detail || 'Request failed: {status}') — it doesn't carry
// the status code, so 404 tolerance matches on the message (same as tutorialRun.js).
const isNotFound = (err) => /404|not found|no active|no run/i.test(err?.message || '');

// POST start. NOT idempotent the way startTutorialRun is, deliberately: when a run
// is already live for this workflow the message is QUEUED onto it, because the
// request carries a MESSAGE rather than naming a fixed script — returning early
// would silently discard what the user just said. Returns the run summary.
//
// `groupId` is the graph group a tile the run creates should join; it defaults
// server-side to `workflowId`. They are separate because `workflowId` is part of a
// DynamoDB key (no `__`) while `groupId` is graph data, and `__default__` is a
// legal group but an illegal workflow id.
//
// `displayMessage` is what the TRANSCRIPT should show when that differs from
// `message` — the composer wraps a typed prompt in kickoff scaffolding
// (`pages/agentKickoff.js`), and showing the wrapper back to the user as their own
// words is what this separates. The model still receives `message` unchanged. Omit
// it and the backend persists `message`, exactly as before.
//
// `agentMode` starts the run in a permission mode chosen BEFORE the project had a
// row to store it on — the dashboard composer's case. `launch.launch_run` reads
// `agent_mode or project.get("agentMode")`, so passing it here wins outright and
// sidesteps the row read: the sticky write and this request race otherwise, and
// turn one would run unmodded. Omit it and the run inherits the row, as before.
export const startAgentRun = async (
  projectId,
  { message, displayMessage, workflowId, groupId, agentMode }
) => {
  const body = { message };
  // `!= null` rather than truthiness: '' is a meaningful value on this field ("show
  // nothing"), and dropping it would put the wrapper back on screen.
  if (displayMessage != null) body.displayMessage = displayMessage;
  if (workflowId) body.workflowId = workflowId;
  if (groupId) body.groupId = groupId;
  if (agentMode) body.agentMode = agentMode;
  const data = await authFetch(`/api/agent-run/${projectId}/start`, {
    method: 'POST',
    body: JSON.stringify(body),
  });
  return data.run;
};

// GET status for one workflow's run. Returns the run, or null when there is none.
export const getAgentRunStatus = async (projectId, workflowId) => {
  const qs = workflowId ? `?workflowId=${encodeURIComponent(workflowId)}` : '';
  try {
    const data = await authFetch(`/api/agent-run/${projectId}/status${qs}`);
    return data.run ?? null;
  } catch (err) {
    if (isNotFound(err)) return null;
    throw err;
  }
};

// GET every agent run in the project — a project may host several workflows, each
// with its own run. Returns an array (possibly empty); 404 tolerated → [].
export const listAgentRuns = async (projectId) => {
  try {
    const data = await authFetch(`/api/agent-run/${projectId}/runs`);
    return data.runs || [];
  } catch (err) {
    if (isNotFound(err)) return [];
    throw err;
  }
};

// POST cancel. Returns the cancelled run, or null when there was none. An
// awaiting_input run IS cancellable — it is not terminal.
export const cancelAgentRun = async (projectId, workflowId) => {
  const qs = workflowId ? `?workflowId=${encodeURIComponent(workflowId)}` : '';
  try {
    const data = await authFetch(`/api/agent-run/${projectId}/cancel${qs}`, { method: 'POST' });
    return data.run ?? null;
  } catch (err) {
    if (isNotFound(err)) return null;
    throw err;
  }
};

// POST pause / resume. A HOLD, not a stop: nothing is torn down, a solve already in
// flight keeps running, and the run stays resumable.
//
// This is the deterministic half of pause. The agent also has a `pause_run` tool so a
// typed "pause" is honoured, but a hold that depends on the model choosing to call a
// tool is not a control — so the button posts here instead.
//
// 404 → null (no run). A 409 (already held / not held / already terminal) is a real
// error and is left to throw: silently swallowing it would leave the button looking
// like it did nothing.
export const pauseAgentRun = async (projectId, workflowId) => {
  const qs = workflowId ? `?workflowId=${encodeURIComponent(workflowId)}` : '';
  try {
    const data = await authFetch(`/api/agent-run/${projectId}/pause${qs}`, { method: 'POST' });
    return data.run ?? null;
  } catch (err) {
    if (isNotFound(err)) return null;
    throw err;
  }
};

export const resumeAgentRun = async (projectId, workflowId) => {
  const qs = workflowId ? `?workflowId=${encodeURIComponent(workflowId)}` : '';
  try {
    const data = await authFetch(`/api/agent-run/${projectId}/resume${qs}`, { method: 'POST' });
    return data.run ?? null;
  } catch (err) {
    if (isNotFound(err)) return null;
    throw err;
  }
};

// POST answer. Un-parks ONE module and hands its subagent the reply.
//
// Keyed by `tileId`, not by a question field: several modules can be parked at
// once (a parked module doesn't stall the run), so the answer has to say which one
// it is for. `value` may be a typed answer OR a bare confirmation — when the
// question was "pick these faces", the user does it in the panel and the module's
// own record is already written, so there is nothing to type and the subagent
// re-reads its state instead.
export const answerAgentRun = async (projectId, { workflowId, tileId, value }) => {
  const body = { tileId, value };
  if (workflowId) body.workflowId = workflowId;
  const data = await authFetch(`/api/agent-run/${projectId}/answer`, {
    method: 'POST',
    body: JSON.stringify(body),
  });
  return data.run ?? null;
};

// Answer the REQUIREMENTS ROUND — every question in one request.
//
// The batch is the point. `ask_user` asks everything at once because one question
// per round trip is what made the orchestrator prefer inventing a material over
// asking about it, so the reply arrives the same way: one request, one turn on the
// run, one write. `answers` is `[{questionId, value}]`; a blank value is a
// legitimate "you decide about this one" and the agent is told which it defaulted.
//
// `skipped: true` is the Skip control — no answers, and the agent proceeds on
// defaults with every invented value marked `assumed` in the plan. Sent as its own
// flag rather than inferred from an empty list because the two mean the same thing
// to the run but the agent should say something different about them.
export const answerAgentQuestions = async (
  projectId,
  { workflowId, answers = [], skipped = false }
) => {
  const body = { answers, skipped };
  if (workflowId) body.workflowId = workflowId;
  const data = await authFetch(`/api/agent-run/${projectId}/questions`, {
    method: 'POST',
    body: JSON.stringify(body),
  });
  return data.run ?? null;
};

// Replace the plan with the user's edited DOCUMENT.
//
// The text is parsed SERVER-side and this client never parses it: one owner of the
// format means the document on screen and the plan on the record cannot come to
// mean different things. A parse failure throws an ApiError whose `detail` carries
// `{message, errors:[{line, message}]}`, so the caller can point at the line —
// and nothing on the record changes, because a partly applied edit is a plan
// nobody wrote.
export const saveAgentPlan = async (projectId, { workflowId, document }) => {
  const body = { document };
  if (workflowId) body.workflowId = workflowId;
  const data = await authFetch(`/api/agent-run/${projectId}/plan`, {
    method: 'POST',
    body: JSON.stringify(body),
  });
  return data.run ?? null;
};

// Decide on one call a MANUAL-mode run is holding. `decision` is 'approve',
// 'approve_always' (also stops asking about that tool for the rest of the run) or
// 'reject'. Keyed by the server-minted `approvalId` rather than by tool: several
// calls can be pending at once, so the decision has to say which — the same reason
// `answerAgentRun` is keyed by tileId. The id being opaque is also what stops a
// client approving a call other than the one it was shown.
export const approveAgentRun = async (projectId, { workflowId, approvalId, decision }) => {
  const body = { approvalId, decision };
  if (workflowId) body.workflowId = workflowId;
  const data = await authFetch(`/api/agent-run/${projectId}/approve`, {
    method: 'POST',
    body: JSON.stringify(body),
  });
  return data.run ?? null;
};

// Set the project's sticky permission mode, and apply it to any live run.
//
// ONE call for both halves, deliberately: the project row is the source of truth
// (so the mode survives a reload) and the live run carries a mirror the supervisor
// enforces from the next tool call. Two separate requests could leave the two
// disagreeing if the second failed. Returns `{agentMode, run}` — `run` is null
// when there is no live run, which is an ordinary outcome, not an error.
export const setAgentMode = async (projectId, { workflowId, mode }) => {
  const body = { mode };
  if (workflowId) body.workflowId = workflowId;
  return authFetch(`/api/agent-run/${projectId}/mode`, {
    method: 'POST',
    body: JSON.stringify(body),
  });
};
